import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Popover, PopoverOrigin } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classnames from 'classnames';
import { DateTime } from 'luxon';
import {
  CalendarPicker,
  PickersDay,
  PickersDayProps
} from '@mui/x-date-pickers';
import { useMeContext } from 'modules/me';
import {
  DEFAULT_WEEKDAY,
  getFirstDayOfWeek,
  WeekDay
} from 'modules/muiPickers/CustomLuxonUtils';
import { getRangeHonoringWeekStartDay } from 'utils/date-range';
import { isWeekend } from 'utils/date-time';

export type WeekPickerProps = {
  outsideCurrentMonth?: boolean;
  value: DateTime | null;
  handleDateChange: (startDate: DateTime, endDate: DateTime) => void;
  minDate?: DateTime;
  startWeek?: WeekDay;
};

const useStyles = makeStyles((theme) => ({
  button: {
    whiteSpace: 'initial',
    textTransform: 'none',
    ...theme.typography.h6,
    padding: theme.spacing(0),
    color: theme.palette.text.primary,
    '&:hover': {
      background: 'transparent'
    }
  },
  weekend: {
    backgroundColor: theme.palette.grey[100]
  },
  dateSelected: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white
  },
  date: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    ...theme.typography.body2
  },
  disableDate: {
    color: theme.palette.grey[500],
    cursor: 'not-allowed'
  },
  hover: {
    backgroundColor: theme.palette.grey[200]
  },
  today: {
    color: theme.palette.primary.main
  },
  selectedDisabled: {
    color: theme.palette.grey[400]
  },
  currentWeek: {
    display: 'flex',
    marginTop: theme.spacing(1),
    marginLeft: 'auto',
    marginRight: 'auto',
    textTransform: 'none'
  }
}));

const usePopoverStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(0, 0, 1.5, 0)
  }
}));

const useCalenderStyles = makeStyles((theme) => ({
  viewTransitionContainer: {
    '& .PrivatePickersSlideTransition-root': {
      minHeight: theme.spacing(29.75)
    }
  }
}));

const usePickerDayStyles = makeStyles((theme) => ({
  root: {
    borderRadius: 0,
    cursor: 'pointer',
    ...theme.typography.body2,
    width: theme.spacing(5),
    height: theme.spacing(4.5),
    margin: 0,
    '&:hover': {
      backgroundColor: theme.palette.grey[200]
    }
  },
  today: {
    borderWidth: '0px !important'
  }
}));

const anchorOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'center'
};
const transformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'center'
};
export const WeekPicker: React.FC<WeekPickerProps> = ({
  value,
  handleDateChange,
  minDate,
  startWeek = DEFAULT_WEEKDAY
}: WeekPickerProps) => {
  const classes = useStyles();
  const popoverClasses = usePopoverStyles();
  const calendarClasses = useCalenderStyles();
  const pickerDayClasses = usePickerDayStyles();
  const me = useMeContext();
  const luxonFormat =
    me?.locale?.dateTimeSettings?.shortDateFormat?.luxonPattern ||
    'MMM d, yyyy';
  const firstDayOfWeek = getFirstDayOfWeek(startWeek);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const dateRange = useMemo(
    () =>
      getRangeHonoringWeekStartDay({
        weekStart: firstDayOfWeek,
        date: value ?? undefined
      }),
    [firstDayOfWeek, value]
  );
  const [hoverDate, setHoverDate] = useState<DateTime | undefined>();
  const customRenderDay = useCallback(
    (
      day: DateTime,
      _: (DateTime | null)[],
      props: PickersDayProps<DateTime>
    ): JSX.Element => {
      const weekend = isWeekend(day?.toJSDate());
      const hoverRange = getRangeHonoringWeekStartDay({
        weekStart: firstDayOfWeek,
        date: hoverDate as DateTime
      });
      const isSelected =
        day &&
        dateRange.start &&
        dateRange.end &&
        day >= dateRange.start &&
        day <= dateRange.end;
      const isDisabled =
        // eslint-disable-next-line react/prop-types
        props.outsideCurrentMonth ||
        (minDate && day && day.toISODate() < minDate.toISODate());
      const isHover =
        !isSelected &&
        hoverDate &&
        day &&
        hoverRange.start &&
        hoverRange.end &&
        day >= hoverRange.start &&
        day <= hoverRange.end;

      return (
        <div
          key={`day_${day.toISODate()}`}
          data-testid={`weekview_${day?.toISODate()}`}
          onMouseEnter={() => setHoverDate(day)}
          onMouseLeave={() => setHoverDate(undefined)}
          className={classnames({
            [classes.hover]:
              isHover && minDate
                ? (hoverRange.end as DateTime) >= minDate
                : isHover,
            [classes.weekend]: weekend,
            [classes.disableDate]: isDisabled
          })}
        >
          <PickersDay
            {...props}
            data-testid={`day_${day?.toISODate()}`}
            disableMargin
            showDaysOutsideCurrentMonth
            selected={isSelected ?? false}
            classes={pickerDayClasses}
            className={classnames(classes.date, {
              [classes.selectedDisabled]: isDisabled && isSelected,
              [classes.disableDate]: isDisabled,
              [classes.hover]:
                isHover && minDate
                  ? (hoverRange.end as DateTime) >= minDate
                  : isHover,
              [classes.weekend]: weekend,
              [classes.dateSelected]: isSelected
            })}
          />
        </div>
      );
    },
    [
      classes,
      dateRange.end,
      dateRange.start,
      firstDayOfWeek,
      hoverDate,
      minDate,
      pickerDayClasses
    ]
  );

  const onDateChange = useCallback(
    (date: DateTime | null) => {
      const dateRange = getRangeHonoringWeekStartDay({
        weekStart: firstDayOfWeek,
        date: date ?? undefined
      });
      handleDateChange(dateRange.start as DateTime, dateRange.end as DateTime);
      handleClose();
    },
    [firstDayOfWeek, handleDateChange]
  );
  const formattedDate = `${dateRange.start?.toFormat(
    luxonFormat
  )} - ${dateRange.end?.toFormat(luxonFormat)}`;

  const defaultDate = useMemo(() => {
    if (!minDate) {
      return value;
    }
    return value && value <= minDate ? minDate.plus({ days: 1 }) : value;
  }, [minDate, value]);

  const goToCurrentWeek = useCallback(() => {
    const dateRange = getRangeHonoringWeekStartDay({
      weekStart: firstDayOfWeek,
      date: DateTime.local()
    });
    handleDateChange(dateRange.start as DateTime, dateRange.end as DateTime);
    handleClose();
  }, [firstDayOfWeek, handleDateChange]);

  const isCurrentWeek = useMemo(() => {
    const { start, end } = dateRange;
    const now = DateTime.local().set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    });

    return (start && end && start <= now && end >= now) ?? false;
  }, [dateRange]);

  return (
    <>
      <Button
        data-testid="week-picker-btn"
        onClick={handleClick}
        className={classes.button}
        disableRipple
        disableFocusRipple
        disableTouchRipple
      >
        {formattedDate}
      </Button>
      <Popover
        data-testid="week-picker"
        classes={popoverClasses}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <CalendarPicker<DateTime>
          classes={calendarClasses}
          date={defaultDate}
          renderDay={customRenderDay}
          minDate={minDate}
          onChange={onDateChange}
        />
        <Button
          data-testid="week-picker-current-week-btn"
          variant="contained"
          onClick={goToCurrentWeek}
          color="primary"
          className={classes.currentWeek}
          disabled={isCurrentWeek}
        >
          <FormattedMessage id="calendar.currentWeek" />
        </Button>
      </Popover>
    </>
  );
};
